import { ImgBackgroundWrapper } from 'components/Common/CommonWrappers';
import { txtAnimation } from 'components/Home/BannerItems';
import { BodyTextL, H3, H3KR, GRAY50 } from 'global-styles';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';

interface Props {
  img: string;
  descriptionTitle: string[];
  descriptions: string[];
  buttons: ReactNode;
}

const ProductDescription = ({
  img,
  descriptionTitle,
  descriptions,
  buttons,
}: Props) => {
  const [targetRef, isVisible] = useInView({ fallbackInView: true });
  const { locale } = useRouter();

  return (
    <ProductDescriptionContainer ref={targetRef} isVisible={isVisible}>
      <div className="left-wrapper">
        <div className="desc-title-wrapper">
          {descriptionTitle.map(title => (
            <React.Fragment key={title}>
              {locale === 'ko' ? (
                <H3KR className="h3-kr-pd" key={title}>
                  {title}
                </H3KR>
              ) : (
                <H3 className="h3-kr-pd" key={title}>
                  {title}
                </H3>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="descriptions-wrapper">
          {descriptions.map(desc => (
            <BodyTextL className="b-t-pd" key={desc}>
              {desc}
            </BodyTextL>
          ))}
        </div>
        <div className="desc-buttons-wrapper">{buttons}</div>
      </div>
      <div className="right-wrapper">
        <ImgBackgroundWrapper isNormalUse>
          <Image
            src={img}
            layout="fill"
            alt=""
            className="target-img"
            objectFit="cover"
            sizes="(min-width: 906px) 600px,
            (min-width: 600px) 95vw,
            (max-width: 599px) 95vw"
            quality={70}
            loading="eager"
            priority
          />
        </ImgBackgroundWrapper>
      </div>
    </ProductDescriptionContainer>
  );
};

export default ProductDescription;

const ProductDescriptionContainer = styled.article<{ isVisible: boolean }>`
  width: 100%;
  /* margin: 146px 0; */
  margin: 146px 0 96px;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  .left-wrapper {
    padding-right: 96px;
  }
  .desc-title-wrapper {
    padding: 32px 0;
  }
  .h3-kr-pd {
    opacity: 0;
  }
  .b-t-pd {
    color: ${GRAY50};
    opacity: 0;
  }
  .b-t-pd + .b-t-pd {
    margin-top: 32px;
  }
  .left-wrapper,
  .right-wrapper {
    width: 50%;
    flex-shrink: 0;
    position: relative;
    max-width: 600px;
  }
  .right-wrapper {
    height: 600px;
    opacity: 0;
    transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .desc-buttons-wrapper {
    display: flex;
    margin-top: 56px;
    opacity: 0;
  }
  .desc-buttons-wrapper > * + * {
    margin-left: 16px;
  }
  ${({ isVisible }) =>
    isVisible &&
    css`
      .h3-kr-pd {
        animation: ${txtAnimation} 1s cubic-bezier(0.4, 0, 0.2, 1) backwards;
        opacity: 1;
      }
      .b-t-pd {
        animation: ${txtAnimation} 1s 0.3s cubic-bezier(0.4, 0, 0.2, 1)
          backwards;
        opacity: 1;
      }
      .desc-buttons-wrapper {
        animation: ${txtAnimation} 1s 0.6s cubic-bezier(0.4, 0, 0.2, 1)
          backwards;
        opacity: 1;
      }
      .right-wrapper {
        opacity: 1;
      }
    `};
  @media screen and (max-width: 1239px) and (min-width: 905px) {
    .left-wrapper {
      padding-right: 32px;
    }
  }

  @media screen and (max-width: 904px) {
    max-height: unset;
    margin: 96px 0 56px;
    flex-direction: column-reverse;
    .left-wrapper {
      max-width: 100%;
      height: calc(100% - 320px);
      width: 100%;
      padding-right: 0;
    }
    .right-wrapper {
      max-width: 100%;
      height: 320px;
      width: 100%;
    }
  }

  @media screen and (max-width: 599px) {
    margin: 56px 0 32px;
  }
`;
